import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { META_MARKET_FLAGS } from './MetaStatsPage';
import { MetaChannelBadge } from '../components/MetaChannelBadge';
import { fmtNum, fmtSpend, fmtPct } from '../lib/metaStatsFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const METRICS = [
  { value: 'spend',       label: 'Spend' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks',      label: 'Clicks' },
];
const MODES = [
  { value: 'absolute', label: 'Absoluta tal' },
  { value: 'percent',  label: 'Andel av total' },
];
const AGE_ORDER = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'Unknown'];
const KON_LABEL = { male: 'Man', female: 'Kvinna', unknown: 'Okänd' };
const KON_COLOR = { male: '#6366f1', female: '#ec4899', unknown: '#9ca3af' };

function pivotByMarket(rows, metric, mode) {
  const byMarket = {};
  rows.forEach(r => {
    if (!byMarket[r.market]) byMarket[r.market] = {};
    const age = r.alder || 'Unknown';
    if (!byMarket[r.market][age]) byMarket[r.market][age] = { age };
    const kon = r.kon || 'unknown';
    byMarket[r.market][age][kon] = (byMarket[r.market][age][kon] || 0) + (r[metric] || 0);
  });
  const result = {};
  Object.entries(byMarket).forEach(([market, ages]) => {
    const rowsForMarket = AGE_ORDER.filter(a => ages[a]).map(a => ages[a]);
    if (mode === 'percent') {
      const total = rowsForMarket.reduce((s, row) => s + (row.male || 0) + (row.female || 0) + (row.unknown || 0), 0) || 1;
      result[market] = rowsForMarket.map(row => ({
        age: row.age,
        male: ((row.male || 0) / total) * 100,
        female: ((row.female || 0) / total) * 100,
        unknown: ((row.unknown || 0) / total) * 100,
      }));
    } else {
      result[market] = rowsForMarket;
    }
  });
  return result;
}

export default function MetaStatsDemographicsTab({ accessToken, marketsParam }) {
  const [metric, setMetric] = useState('spend');
  const [mode, setMode] = useState('absolute');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/meta-stats/demographics?markets=${marketsParam}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError('Kunde inte hämta demografidata.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, marketsParam]);

  useEffect(() => { load(); }, [load]);

  const chartsByMarket = useMemo(() => {
    if (!data) return {};
    return pivotByMarket(data.demographics, metric, mode);
  }, [data, metric, mode]);

  const fmt = mode === 'percent' ? (v) => fmtPct(v) : (metric === 'spend' ? fmtSpend : fmtNum);

  return (
    <div data-testid="metastats-demographics-tab">
      <div className="mstat-tab-badge-row"><MetaChannelBadge type="paid" label="Betald målgrupp" /></div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="tab-switcher" style={{ margin: 0 }}>
          {METRICS.map(m => (
            <button
              key={m.value}
              data-testid={`metastats-demo-metric-${m.value}`}
              className={`tab-btn${metric === m.value ? ' tab-btn--active' : ''}`}
              style={{ fontSize: 12 }}
              onClick={() => setMetric(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="tab-switcher" style={{ margin: 0 }}>
          {MODES.map(m => (
            <button
              key={m.value}
              data-testid={`metastats-demo-mode-${m.value}`}
              className={`tab-btn${mode === m.value ? ' tab-btn--active' : ''}`}
              style={{ fontSize: 12 }}
              onClick={() => setMode(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="admin-notice" data-testid="metastats-demographics-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Laddar demografidata...</div>
      ) : Object.keys(chartsByMarket).length === 0 ? (
        <div className="admin-empty" data-testid="metastats-demographics-empty">Ingen demografidata för valda marknader.</div>
      ) : (
        <div className="mstat-demo-grid" data-testid="metastats-demo-grid">
          {Object.entries(chartsByMarket).map(([market, rows]) => (
            <div className="mstat-demo-card" key={market} data-testid={`metastats-demo-chart-${market}`}>
              <h3 className="mstat-demo-card-title">
                {META_MARKET_FLAGS[market] || ''} {market}
              </h3>
              {rows.length === 0 ? (
                <div className="admin-empty">Ingen data.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="age" fontSize={10} />
                    <YAxis fontSize={10} tickFormatter={mode === 'percent' ? (v) => `${v}%` : undefined} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => KON_LABEL[v] || v} />
                    <Bar dataKey="male" name="male" fill={KON_COLOR.male} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="female" name="female" fill={KON_COLOR.female} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unknown" name="unknown" fill={KON_COLOR.unknown} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
